import type { WaterKind } from '../../site/types';

export const SURFACE = {
  ground: 0,
  field: 1,
  garden: 2,
  street: 3,
  plaster: 4,
  brick: 5,
  stone: 6,
  wood: 7,
  roofTile: 8,
  roofSlate: 9,
  roofThatch: 10,
  foliage: 11,
  sail: 12,
  cloth: 13,
  linework: 14,
  quayEarth: 15,
  smoke: 16,
} as const;

export type SurfaceKind = (typeof SURFACE)[keyof typeof SURFACE];

export const ATTRIBUTE = {
  color: 'color',
  facade: 'aFacade',
  meta: 'aMeta',
  instanceEdgeId: 'aInstanceEdgeId',
  exaggeration: 'aExaggeration',
  waterKind: 'aWaterKind',
} as const;

// aWaterKind, on the water mesh only, is one unnormalised byte per vertex that shaders read as a float:
// the site's kind of water at the probe that also picks the vertex colour. The open water comes first,
// so the IJ and its harbour are the codes below 2. Codes interpolate like the colour: where the class
// changes they turn within 2 m (KIND_BOUNDARY_EDGE_M), but kinds of one class blend across whole
// triangles where they meet, the IJ and its harbour among them; a test below 1.5 has no such ramp.
export const WATER_KIND_CODE = {
  ij: 0,
  harbour: 1,
  river: 2,
  canal: 3,
  moat: 4,
  ditch: 5,
  area: 6,
} as const satisfies Readonly<Record<WaterKind, number>>;

// Edge ids ride a half-float G-buffer channel, exact to 2048. The vessels hold the top ids apart
// from every other object so final.frag can outline a ship against the water it floats on
// (STYLE §5.2); 256 of them give each of the 217 ships of the real fleet an id of its own.
export const EDGE_ID = {
  sky: 0,
  water: 1,
  firstObject: 16,
  firstVessel: 1792,
  max: 2047,
} as const;

export function edgeIdFor(index: number): number {
  const range = EDGE_ID.firstVessel - EDGE_ID.firstObject;
  return EDGE_ID.firstObject + (index % range);
}

export function vesselEdgeIdFor(index: number): number {
  const range = EDGE_ID.max - EDGE_ID.firstVessel + 1;
  return EDGE_ID.firstVessel + (index % range);
}
